/**
 * REPORTS PIVOT TRANSFORMER SERVICE
 * Transforms raw API data into pivoted table format
 * Handles all report types with generic logic for maximum reusability
 */

import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  DisbursementReportRawData,
  DisbursementReportRow,
  RepaymentReportRow,
  ExposureReportRow,
  PivotedReportData,
} from '../models/report.model';

@Injectable({ providedIn: 'root' })
export class ReportPivotService {
  /**
   * Transforms raw repayment data into simple rows (not pivoted)
   * One row per payment record, shows KIBOR, Spread, Rate info
   *
   * @param rawData - API response array
   * @returns Simple rows ready for display
   */
  transformRepaymentReport(rawData: DisbursementReportRawData[]): RepaymentReportRow[] {
    if (!rawData || rawData.length === 0) return [];

    // Filter to only paid records (paidAmt > 0)
    const paidRecords = rawData.filter(
      (row) => (row.paidAmt && row.paidAmt > 0) || (row.markupPaid && row.markupPaid > 0),
    );
    let srNo = 1;
    return paidRecords.map((record) => ({
      srNo: srNo++,
      fcltySeq: record.fcltySeq,
      borrowerName: record.borrowerName.trim(),
      fcltyTyp: record.fcltyTyp,
      phsKey: record.phsKey,
      fcltyAmount: record.fcltyAmount,
      spread: record.spread,
      kiborRt: record.kiborRt,
      totalRt: record.totalRt,
      repaymentDate: this.formatDate(record.paidDt),
      repaymentAmount: record.paidAmt,
      markupPaidAmount: record.markupPaid || 0,
      markupPaidDate: this.formatDate(record.markupPaidDt),
    }));
  }

  /**
   * Transforms raw disbursement data into pivoted table rows
   * Generic enough for any quarter-based report
   * Overloaded signatures for type safety
   *
   * @param rawData - API response array
   * @param valueField - Which field to use for quarter columns (disursmentAmount, dueAmt, paidAmt, closingExposure)
   * @returns Pivoted rows ready for display
   */
  transformDisbursementReport(
    rawData: DisbursementReportRawData[],
    valueField: 'disursmentAmount' | 'dueAmt' | 'paidAmt',
  ): DisbursementReportRow[];
  transformDisbursementReport(
    rawData: DisbursementReportRawData[],
    valueField: 'closingExposure',
  ): ExposureReportRow[];
  transformDisbursementReport(
    rawData: DisbursementReportRawData[],
    valueField: 'disursmentAmount' | 'dueAmt' | 'paidAmt' | 'closingExposure' = 'disursmentAmount',
  ): (DisbursementReportRow | ExposureReportRow)[] {
    if (!rawData || rawData.length === 0) return [];

    // Step 1: Group by facility + phase + disbursement date
    // (one facility can have multiple phases, and each phase can have multiple disbursements)
    const facilitiesMap = new Map<string, DisbursementReportRawData[]>();
    const quartersSet = new Set<string>();

    rawData.forEach((row) => {
      // Use composite key: facility sequence + phase + disbursement date
      const dsbmntDate = this.formatDate(row.dsbmntDt);
      const fcltyKey = `${row.fcltySeq}|${row.phsKey}|${dsbmntDate}`;
      if (!facilitiesMap.has(fcltyKey)) {
        facilitiesMap.set(fcltyKey, []);
      }
      facilitiesMap.get(fcltyKey)!.push(row);
      quartersSet.add(row.quarterBucket);
    });

    // Step 2: Sort quarters chronologically
    const quartersSorted = Array.from(quartersSet).sort((a, b) => this.compareQuarters(a, b));

    // Step 3: Transform into rows
    let srNo = 1;
    const rows: (DisbursementReportRow | ExposureReportRow)[] = [];

    facilitiesMap.forEach((facilityRecords) => {
      if (facilityRecords.length === 0) return;

      const first = facilityRecords[0];
      const uniqueRepayments = new Set<string>();
      let totalRepayments = 0;

      facilityRecords.forEach((record) => {
        if (!record.paidAmt || record.paidAmt <= 0) {
          return;
        }

        const key = `${record.fcltySeq}|${record.paidAmt}`;

        if (!uniqueRepayments.has(key)) {
          uniqueRepayments.add(key);
          totalRepayments += record.paidAmt;
        }
      });
      const quarterData: { [key: string]: number } = {};
      let total = 0;

      // Initialize all quarters with 0
      quartersSorted.forEach((quarter) => {
        quarterData[quarter] = 0;
      });
      const processedBuckets = new Set<string>();

      // Fill in actual values from records
      facilityRecords.forEach((record) => {
        let value = 0;

        // Exposure report aging buckets
        if (valueField === 'closingExposure') {
          const dueAmt = record.dueAmt || 0;

          // Show only pending/outstanding amount
          value = dueAmt;

          // If fully paid then hide bucket
          if (value <= 0) {
            value = 0;
          }
        } else {
          value = record[valueField] || 0;
        }

        if (!processedBuckets.has(record.quarterBucket)) {
          processedBuckets.add(record.quarterBucket);

          quarterData[record.quarterBucket] = value;
          total += value;
        }
      });

      const baseRow = {
        srNo: srNo++,
        fcltySeq: first.fcltySeq,
        borrowerName: first.borrowerName.trim(),
        fcltyTyp: first.fcltyTyp,
        phsKey: first.phsKey,
        fcltyAmount: first.fcltyAmount,
        fcltyEndDt: this.formatDate(first.fcltyEndDt),
        kbrResetPeriod: first.kbrResetPeriod,
        kbrPeriod: first.kbrPeriod,
        spread: first.spread,
        kiborRt: first.kiborRt,
        totalRt: first.totalRt,
        opening: first.opening || 0,
        markup: first.markup,
        quarterData,
      };

      if (valueField === 'closingExposure') {
        // Exposure Report: include repaymentAmount, disursmentAmount, and closingExposure
        const openingExposure = first.opening || 0;
        const disbursementAmount = first.disursmentAmount || 0;

        const closingExposure = openingExposure + disbursementAmount - totalRepayments;

        rows.push({
          ...baseRow,
          repaymentAmount: totalRepayments,
          disursmentAmount: disbursementAmount,
          closingExposure,
          total,
        } as ExposureReportRow);
      } else {
        // Disbursement Report: include dsbmntDt and disursmentAmount
        rows.push({
          ...baseRow,
          dsbmntDt: this.formatDate(first.dsbmntDt),
          disursmentAmount: first.disursmentAmount || 0,
          total,
        } as DisbursementReportRow);
      }
    });

    return rows;
  }

  /**
   * Gets unique quarters in order from raw data
   */
  getQuarterColumns(rawData: DisbursementReportRawData[]): string[] {
    const quarters = new Set<string>();
    rawData.forEach((row) => quarters.add(row.quarterBucket));
    return Array.from(quarters).sort((a, b) => this.compareQuarters(a, b));
  }

  /**
   * Compares two quarter strings for sorting
   * e.g., "Oct-25" vs "Jan-26"
   */
  private compareQuarters(a: string, b: string): number {
    const monthMap: { [key: string]: number } = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };

    const [aMonth, aYear] = a.split('-');
    const [bMonth, bYear] = b.split('-');

    const aYearNum = parseInt(aYear, 10);
    const bYearNum = parseInt(bYear, 10);

    if (aYearNum !== bYearNum) {
      return aYearNum - bYearNum;
    }

    return (monthMap[aMonth] || 0) - (monthMap[bMonth] || 0);
  }

  /**
   * Formats date string to YYYY-MM-DD
   */
  private formatDate(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';
    try {
      const date = new Date(dateInput);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  }

  /**
   * Formats large numbers with commas for display
   * Preserves 2 decimal places for precision
   */
  formatCurrency(value: number): string {
    if (value == null) return '0.00';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Calculates total for all rows
   */
  calculateGrandTotal(rows: DisbursementReportRow[]): number {
    return rows.reduce((sum, row) => sum + row.total, 0);
  }

  /**
   * Rounds a numeric value to a whole number, matching the UI's rounded display.
   * Excel cells store this rounded value directly (not just a display numFmt),
   * so the exported file matches the UI exactly regardless of how it's opened.
   */
  private roundAmount(value: number | null | undefined): number {
    return Math.round(value || 0);
  }

  /**
   * Exports report data as a styled Excel (.xlsx) file
   */
  exportToCSV(
    rows: (DisbursementReportRow | ExposureReportRow)[],
    quarterColumns: string[],
    filename: string = 'report.xlsx',
    isExposureReport: boolean = false,
    startDate: Date | string | null = null,
    endDate: Date | string | null = null,
  ): void {
    const xlsxFilename = filename.replace('.csv', '.xlsx');

    const reportTitle = isExposureReport ? 'Exposure Report' : 'Disbursement Report';

    const formattedStartDate = this.formatExportDate(startDate);
    const formattedEndDate = this.formatExportDate(endDate);

    const dateRangeText =
      `From Date: ${formattedStartDate || '-'}    ` + `To Date: ${formattedEndDate || '-'}`;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Report');

    const headerColumns = isExposureReport
      ? [
          'Sr No.',
          'Borrower Name',
          'Facility Type',
          'Phase',
          'Facility Amount',
          'Facility End Date',
          'KIBOR Period',
          'Spread %',
          'KIBOR Rate %',
          'Total Rate %',
          'Opening Exposure',
          'Disbursement Amount',
          'Repayments',
          'Closing Exposure',
          'Markup',
          ...quarterColumns,
          'Total',
        ]
      : [
          'Sr No.',
          'Borrower Name',
          'Facility Type',
          'Phase',
          'Facility Amount',
          'Facility End Date',
          'KIBOR Period',
          'Spread %',
          'KIBOR Rate %',
          'Total Rate %',
          'Disbursement Date',
          'Disbursement Amount',
          'Markup',
          ...quarterColumns,
          'Total',
        ];
    const totalCols = headerColumns.length;

    // ── Row 1: Report title ──
    sheet.addRow([reportTitle]);
    sheet.mergeCells(1, 1, 1, totalCols);

    const titleCell = sheet.getCell('A1');

    titleCell.font = {
      name: 'Calibri',
      bold: true,
      size: 14,
      color: { argb: 'FF1E3A8A' },
    };

    titleCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };

    sheet.getRow(1).height = 28;

    // ── Row 2: From Date / To Date ──
    sheet.addRow([dateRangeText]);
    sheet.mergeCells(2, 1, 2, totalCols);

    const dateCell = sheet.getCell('A2');

    dateCell.font = {
      name: 'Calibri',
      bold: true,
      size: 11,
      color: { argb: 'FF444A6D' },
    };

    dateCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };

    dateCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF0F4FF' },
    };

    sheet.getRow(2).height = 22;

    // ── Row 3: Table headers ──
    const headerRow = sheet.addRow(headerColumns);

    headerRow.height = 30;

    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1E3A8A' },
      };

      cell.font = {
        name: 'Calibri',
        bold: true,
        size: 11,
        color: { argb: 'FFFFFFFF' },
      };

      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };

      cell.border = {
        top: { style: 'thin', color: { argb: 'FF1E3A8A' } },
        bottom: { style: 'thin', color: { argb: 'FF1E3A8A' } },
        left: { style: 'thin', color: { argb: 'FF1E3A8A' } },
        right: { style: 'thin', color: { argb: 'FF1E3A8A' } },
      };
    });

    // ── Rows 3+: Data ──
    rows.forEach((row, idx) => {
      const isOdd = idx % 2 === 0;
      const rowBg = isOdd ? 'FFF0F4FF' : 'FFFFFFFF';

      const dataRow = sheet.addRow(
        isExposureReport
          ? [
              row.srNo,
              row.borrowerName,
              row.fcltyTyp,
              row.phsKey,
              this.roundAmount(row.fcltyAmount),
              row.fcltyEndDt,
              row.kbrPeriod,
              row.spread || 0,
              row.kiborRt || 0,
              row.totalRt || 0,
              this.roundAmount(row.opening),
              this.roundAmount((row as ExposureReportRow).disursmentAmount),
              this.roundAmount((row as ExposureReportRow).repaymentAmount),
              this.roundAmount((row as ExposureReportRow).closingExposure),
              this.roundAmount(row.markup),
              ...quarterColumns.map((q) => this.roundAmount(row.quarterData[q])),
              this.roundAmount(row.total),
            ]
          : [
              row.srNo,
              row.borrowerName,
              row.fcltyTyp,
              row.phsKey,
              this.roundAmount(row.fcltyAmount),
              row.fcltyEndDt,
              row.kbrPeriod,
              row.spread || 0,
              row.kiborRt || 0,
              row.totalRt || 0,
              (row as DisbursementReportRow).dsbmntDt,
              this.roundAmount((row as DisbursementReportRow).disursmentAmount),
              this.roundAmount(row.markup),
              ...quarterColumns.map((q) => this.roundAmount(row.quarterData[q])),
              this.roundAmount(row.total),
            ],
      );

      dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
        cell.font = { name: 'Calibri', size: 10, bold: isOdd };
        cell.border = {
          top: { style: 'hair', color: { argb: 'FFD0D7F0' } },
          bottom: { style: 'hair', color: { argb: 'FFD0D7F0' } },
          left: { style: 'hair', color: { argb: 'FFD0D7F0' } },
          right: { style: 'hair', color: { argb: 'FFD0D7F0' } },
        };
        // Number formatting for currency columns
        let currencyCols: number[];
        const percentageCols = [8, 9, 10];

        if (isExposureReport) {
          // Exposure: 5(fcltyAmount), 11(opening), 12(disursmentAmount), 13(repaymentAmount), 14(closingExposure), 15(markup), 16+(quarters), totalCols(total)
          currencyCols = [
            5,
            11,
            12,
            13,
            14,
            15,
            ...quarterColumns.map((_, i) => 16 + i),
            totalCols,
          ];
        } else {
          // Disbursement: 5(fcltyAmount), 11(dsbmntDt-text), 12(disursmentAmount), 13(markup), 14+(quarters), totalCols(total)
          currencyCols = [5, 12, 13, ...quarterColumns.map((_, i) => 14 + i), totalCols];
        }

        if (currencyCols.includes(colNumber)) {
          cell.numFmt = '#,##0';
          cell.alignment = { horizontal: 'right' };
        }
        if (percentageCols.includes(colNumber)) {
          cell.numFmt = '0.00';
          cell.alignment = { horizontal: 'right' };
        }
      });
    });

    // ── Grand Total Row ──
    const grandTotalData = isExposureReport
      ? [
          '',
          'GRAND TOTAL',
          '',
          '',
          this.roundAmount(rows.reduce((s, r) => s + (r.fcltyAmount || 0), 0)),
          '',
          '',
          '',
          '',
          '',
          this.roundAmount(rows.reduce((s, r) => s + (r.opening || 0), 0)),
          this.roundAmount(
            rows.reduce((s, r) => s + ((r as ExposureReportRow).disursmentAmount || 0), 0),
          ),
          this.roundAmount(
            rows.reduce((s, r) => s + ((r as ExposureReportRow).repaymentAmount || 0), 0),
          ),
          this.roundAmount(
            rows.reduce((s, r) => s + ((r as ExposureReportRow).closingExposure || 0), 0),
          ),
          this.roundAmount(rows.reduce((s, r) => s + (r.markup || 0), 0)),
          ...quarterColumns.map((q) =>
            this.roundAmount(rows.reduce((s, r) => s + (r.quarterData[q] || 0), 0)),
          ),
          this.roundAmount(rows.reduce((s, r) => s + r.total, 0)),
        ]
      : [
          '',
          'GRAND TOTAL',
          '',
          '',
          this.roundAmount(rows.reduce((s, r) => s + (r.fcltyAmount || 0), 0)),
          '',
          '',
          '',
          '',
          '',
          '',
          this.roundAmount(
            rows.reduce((s, r) => s + ((r as DisbursementReportRow).disursmentAmount || 0), 0),
          ),
          this.roundAmount(rows.reduce((s, r) => s + (r.markup || 0), 0)),
          ...quarterColumns.map((q) =>
            this.roundAmount(rows.reduce((s, r) => s + (r.quarterData[q] || 0), 0)),
          ),
          this.roundAmount(rows.reduce((s, r) => s + r.total, 0)),
        ];
    const totalRow = sheet.addRow(grandTotalData);
    totalRow.height = 22;
    totalRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDFF' } };
      cell.font = { name: 'Calibri', bold: true, size: 10, color: { argb: 'FF1E3A8A' } };
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF1E3A8A' } },
        bottom: { style: 'medium', color: { argb: 'FF1E3A8A' } },
        left: { style: 'thin', color: { argb: 'FFD0D7F0' } },
        right: { style: 'thin', color: { argb: 'FFD0D7F0' } },
      };
      // Updated currency columns for new field layout
      let currencyCols: number[];
      const percentageCols = [8, 9, 10];
      if (isExposureReport) {
        // Exposure: 5(fcltyAmount), 11(opening), 12(disursmentAmount), 13(repaymentAmount), 14(closingExposure), 15(markup), 16+(quarters), totalCols
        currencyCols = [5, 11, 12, 13, 14, 15, ...quarterColumns.map((_, i) => 16 + i), totalCols];
      } else {
        // Disbursement: 5(fcltyAmount), 12(disursmentAmount), 13(markup), 14+(quarters), totalCols
        currencyCols = [5, 12, 13, ...quarterColumns.map((_, i) => 14 + i), totalCols];
      }
      if (currencyCols.includes(colNumber)) {
        cell.numFmt = '#,##0';
        cell.alignment = { horizontal: 'right' };
      }
      if (percentageCols.includes(colNumber)) {
        cell.numFmt = '0.00';
        cell.alignment = { horizontal: 'right' };
      }
    });

    // ── Column widths ──
    const colWidths = [
      8,
      32,
      16,
      8,
      18,
      16,
      16,
      12,
      12,
      12,
      16,
      16,
      18,
      30,
      ...quarterColumns.map(() => 14),
      35,
    ];
    sheet.columns.forEach((col, idx) => {
      col.width = colWidths[idx] ?? 14;
    });
    sheet.getColumn(14).width = 40; // Closing Exposure
    sheet.getColumn(totalCols).width = 40; // Total

    // ── Download ──
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, xlsxFilename);
    });
  }

  /**
   * Exports repayment report data as a styled Excel (.xlsx) file
   */
  exportRepaymentReportToExcel(
    rows: RepaymentReportRow[],
    filename: string = 'repayment-report.xlsx',
    startDate: Date | string | null = null,
    endDate: Date | string | null = null,
  ): void {
    const xlsxFilename = filename.replace('.csv', '.xlsx');

    const reportTitle = 'Repayment Report';

    const formattedStartDate = this.formatExportDate(startDate);
    const formattedEndDate = this.formatExportDate(endDate);

    const dateRangeText =
      formattedStartDate && formattedEndDate
        ? `From Date: ${formattedStartDate}    To Date: ${formattedEndDate}`
        : '';

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Repayment Report');

   const headerColumns = [
  'Sr No.',
  'Borrower Name',
  'Facility Type',
  'Phase',
  'Facility Amount',
  'Spread%',
  'Kibor Rate %',
  'Total Rate %',
  'Markup Paid Date',
  'Markup Paid',
  'Repayment Date',
  'Principal Repayment Amount',
];

    const totalCols = headerColumns.length;

    // Row 1: Report Title
    sheet.addRow([reportTitle]);
    sheet.mergeCells(1, 1, 1, totalCols);

    const titleCell = sheet.getCell('A1');

    titleCell.font = {
      name: 'Calibri',
      bold: true,
      size: 14,
      color: { argb: 'FF1E3A8A' },
    };

    titleCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };

    sheet.getRow(1).height = 28;

    // Row 2: From Date / To Date
    sheet.addRow([dateRangeText]);
    sheet.mergeCells(2, 1, 2, totalCols);

    const dateCell = sheet.getCell('A2');

    dateCell.font = {
      name: 'Calibri',
      bold: true,
      size: 11,
      color: { argb: 'FF444A6D' },
    };

    dateCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };

    dateCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF0F4FF' },
    };

    sheet.getRow(2).height = 22;

    // Row 3: Column Headers
    const headerRow = sheet.addRow(headerColumns);

    headerRow.height = 30;

    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1E3A8A' },
      };

      cell.font = {
        name: 'Calibri',
        bold: true,
        size: 11,
        color: { argb: 'FFFFFFFF' },
      };

      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };

      cell.border = {
        top: { style: 'thin', color: { argb: 'FF1E3A8A' } },
        bottom: { style: 'thin', color: { argb: 'FF1E3A8A' } },
        left: { style: 'thin', color: { argb: 'FF1E3A8A' } },
        right: { style: 'thin', color: { argb: 'FF1E3A8A' } },
      };
    });

    // Row 4+: Data Rows
    rows.forEach((row, index) => {
      const isOdd = index % 2 === 0;
      const rowBackground = isOdd ? 'FFF0F4FF' : 'FFFFFFFF';

     const dataRow = sheet.addRow([
  row.srNo,
  row.borrowerName,
  row.fcltyTyp,
  row.phsKey,
  this.roundAmount(row.fcltyAmount),
  row.spread || 0,
  row.kiborRt || 0,
  row.totalRt || 0,
  row.markupPaidDate || '',
  this.roundAmount(row.markupPaidAmount),
  row.repaymentDate || '',
  this.roundAmount(row.repaymentAmount),
]);

      dataRow.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: rowBackground },
        };

        cell.font = {
          name: 'Calibri',
          size: 10,
          bold: isOdd,
        };

        cell.border = {
          top: { style: 'hair', color: { argb: 'FFD0D7F0' } },
          bottom: { style: 'hair', color: { argb: 'FFD0D7F0' } },
          left: { style: 'hair', color: { argb: 'FFD0D7F0' } },
          right: { style: 'hair', color: { argb: 'FFD0D7F0' } },
        };

        // Facility Amount, Markup Paid, Repayment Amount
        if ([5, 10, 12].includes(columnNumber)) {
  cell.numFmt = '#,##0';
  cell.alignment = { horizontal: 'right' };
}

        // Spread, KIBOR Rate, Total Rate
        if ([6, 7, 8].includes(columnNumber)) {
          cell.numFmt = '0.00';
          cell.alignment = { horizontal: 'right' };
        }
      });
    });

    // Grand Total Row
    const grandTotalData = [
  '',
  'GRAND TOTAL',
  '',
  '',
  this.roundAmount(rows.reduce((sum, row) => sum + (row.fcltyAmount || 0), 0)),
  '',
  '',
  '',
  '',
  this.roundAmount(rows.reduce((sum, row) => sum + (row.markupPaidAmount || 0), 0)),
  '',
  this.roundAmount(rows.reduce((sum, row) => sum + (row.repaymentAmount || 0), 0)),
];

    const totalRow = sheet.addRow(grandTotalData);

    totalRow.height = 22;

    totalRow.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8EDFF' },
      };

      cell.font = {
        name: 'Calibri',
        bold: true,
        size: 10,
        color: { argb: 'FF1E3A8A' },
      };

      cell.border = {
        top: { style: 'medium', color: { argb: 'FF1E3A8A' } },
        bottom: { style: 'medium', color: { argb: 'FF1E3A8A' } },
        left: { style: 'thin', color: { argb: 'FFD0D7F0' } },
        right: { style: 'thin', color: { argb: 'FFD0D7F0' } },
      };

     if ([5, 10, 12].includes(columnNumber)) {
  cell.numFmt = '#,##0';
  cell.alignment = { horizontal: 'right' };
}
    });

    // Column Widths
    const columnWidths = [8, 32, 16, 8, 18, 12, 14, 14, 26, 18, 18, 28];

    sheet.columns.forEach((column, index) => {
      column.width = columnWidths[index] ?? 14;
    });

    // Freeze top three rows
    sheet.views = [
      {
        state: 'frozen',
        ySplit: 3,
      },
    ];

    // Download
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, xlsxFilename);
    });
  }

  private formatExportDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }
}
